/**
 * StudioMcpBus — wraps CogoToolRegistry with namespace enforcement
 * and middleware chain support for cogo.studio.* commands.
 */

import {
  CogoToolRegistry,
  type WebMCPTool,
  type ToolManifestEntry,
  type ToolsChangedListener,
} from '@cogo/webmcp-runtime'
import {
  type McpCommand,
  type McpResult,
  type Middleware,
  type StudioCapabilityApprovalVerifier,
  STUDIO_NS,
} from './types'
import { notificationBus } from './notification-bus'
import { getLocale } from '@/pages/cogo-marketing/i18n/locale-store'
import { getChatMessagesCopy } from '@/shell/ide-v2/components/chat-messages-copy'
import type { ChatMessage } from '@/features/channel-router/chat-message'

/**
 * Dynamic, not static. A static `import { useStudioStore } from
 * '@/store/studio-store'` here (added 2026-08-17 by `eb7307522`, the central
 * agent-disclosure fix) is the ARTICULATION POINT of the
 * `project-slice -> ... -> studio-store -> project-slice` import cycle that
 * `store/__tests__/project-slice-circular-import.test.ts` guards: it is the
 * ONE edge whose removal closes EVERY path from `project-slice.ts` to
 * `studio-store.ts`. Cutting either other candidate edge leaves the cycle
 * open, because redundant routes (`store/auto-save-bridge.ts`, and
 * `features/flow-editor/store/flow-editor-store.ts` -> `mcp/init-undo.ts`)
 * both converge back onto this same edge — measured, not assumed.
 *
 * Why the cycle bites at all: `studio-store.ts` calls `create()` at module
 * top level and spreads `createProjectSlice(...)` in, and under vite-node's
 * SSR transform an export binding is installed in BODY order rather than
 * hoisted the way a real-ESM `export function` declaration would be. (Also
 * measured: a two-module fixture with a hoisted `export function` across a
 * cycle throws the same `TypeError: ... is not a function` under this
 * runner.) So whenever anything other than `studio-store.ts` is first into
 * the graph, `createProjectSlice` is still undefined when `studio-store.ts`
 * evaluates.
 *
 * Same mechanism, and same reason, as
 * `features/page-editor/store/selection-slice.ts`'s `getStudioStore()`.
 * Deliberately a dynamic import rather than a registered-sink leaf module:
 * `vi.mock('@/store/studio-store')` still intercepts a dynamic import, so
 * the ~33 bus tests that mock the store keep the exact behaviour they had
 * under the static import. A sink would have bypassed those mocks entirely.
 */
async function getStudioStore() {
  const { useStudioStore } = await import('@/store/studio-store')
  return useStudioStore
}

const CAPABILITY_APPROVAL_TOKEN_KEY = '_capabilityApprovalToken'

type StudioMcpExecutionAuthority =
  | 'internal'
  | 'studio-ui'
  | 'webmcp'

export interface StudioMcpExecutionOptions {
  readonly signal?: AbortSignal
  /**
   * Wave 4 lane 1 (studio-slice4 "38/44 disclosure" finding — see
   * docs/superpowers/specs/2026-08-17-studio-slice4-disclosure-authority.md
   * §5/§6). Set by a CALL SITE that already appends its own success-path
   * `ChatMessage` for this exact dispatch (today: `lib/studio-bridge.ts`'s
   * `safeDispatch`, whose `pushMutationApplied`/`pushMutationFailed` fire
   * for tools in `MUTATION_OUTCOME_TOOL_MAP`). `executeWithAuthority` reads
   * this flag to suppress its OWN disclosure for that one call — structural
   * suppression keyed off a marker the site itself computes and sets, not a
   * tool-name/file:line list maintained inside the bus. Every other caller
   * (chat's `capability.executeOperation` plan executor, UI-triggered
   * writes, …) leaves this unset and gets the central disclosure.
   */
  readonly siteHandlesDisclosure?: boolean
}

const SENSITIVE_PARAM_KEYS = new Set([
  '_abortSignal',
  '_capabilityApprovalToken',
  'accessToken',
  'apiKey',
  'authorization',
  'authToken',
  'figmaAccessToken',
  'password',
  'secret',
  'token',
])

function sanitizeNotificationParams(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeNotificationParams(item))
  }
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
      key,
      SENSITIVE_PARAM_KEYS.has(key) ? '[redacted]' : sanitizeNotificationParams(entry),
    ]),
  )
}

/**
 * Cheap, best-effort target identifier for the central disclosure card (Wave
 * 4 lane 1). "Cheap" per the fix's own constraint: a plain-object field
 * lookup over the command's own params, never a registry/store read or a
 * shape-specific per-tool mapping (that already exists, narrowly, in
 * `lib/studio-bridge.ts`'s `pushMutationApplied`, which this central path
 * deliberately does not replicate). Absence is a legitimate outcome — the
 * disclosure card renders with just the tool name when no candidate field is
 * a non-empty string.
 */
const CHEAP_TARGET_PARAM_KEYS = [
  'elementId',
  'flowId',
  'nodeId',
  'pageId',
  'variableId',
  'datasourceId',
  'id',
  'name',
] as const

function extractCheapTargetSummary(params: unknown): string | null {
  if (!params || typeof params !== 'object') return null
  const record = params as Record<string, unknown>
  for (const key of CHEAP_TARGET_PARAM_KEYS) {
    const value = record[key]
    if (typeof value === 'string' && value.length > 0) return value
  }
  return null
}

/**
 * Explicit, human-reviewed opt-out from the registry-derived WebMCP approval
 * floor (see `StudioMcpBus.requiresCapabilityApproval`). Every entry must
 * document WHY it is safe to bypass the fail-closed default — either an
 * equivalent boundary is enforced elsewhere (the tool's own handler) or the
 * tool is genuinely non-mutating despite its manifest metadata not (yet)
 * declaring `readOnly: true`. Same exact-match / mechanism-reason /
 * review-date idiom as the allowlists in
 * `scripts/lib/__tests__/locale-key-parity.gate.test.ts` — a hand-maintained
 * EXCEPTION list, never a hand-maintained DEFINITION of what's gated (that
 * definition is the live registry scan in `requiresCapabilityApproval`
 * below).
 *
 * Closes the "82-tool operation-contract gap" — see
 * docs/superpowers/specs/2026-08-17-studio-slice4-disclosure-authority.md
 * §4.2. Before this fix, BOTH webmcp-authority gates (this bus's own
 * approval check, and `wrapStudioMcpHandler`'s pre-check in
 * `agent/webmcp-setup.ts`, which delegates to this bus) derived their gated
 * universe from the chat-operation-contract list alone, so any registered
 * mutating tool with no contract silently skipped both. The floor below
 * makes contract absence fail CLOSED (gated by default) instead of open.
 */
export interface WebMcpApprovalOptOutEntry {
  /** Full `cogo.studio.*` tool name (namespace included). */
  tool: string
  reason: string
}

export const WEBMCP_APPROVAL_OPT_OUT_TOOLS: readonly WebMcpApprovalOptOutEntry[] = [
  {
    tool: `${STUDIO_NS}apply_openpencil_cogo_design_mutation`,
    reason:
      "Handler enforces its own approval boundary via consumeStudioCapabilityApprovalGrant " +
      'before applying the mutation (studio-capability-gateway.ts HANDLER_ENFORCED_APPROVAL_TOOLS) ' +
      '— a second, redundant bus-level block would only degrade the error message, not add safety. ' +
      'Reviewed 2026-08-17 (Wave 3 lane B, disclosure+authority gap repair).',
  },
  {
    tool: `${STUDIO_NS}import_figma_frame`,
    reason:
      'Handler enforces its own approval boundary the same way as ' +
      'apply_openpencil_cogo_design_mutation above (HANDLER_ENFORCED_APPROVAL_TOOLS). ' +
      'Reviewed 2026-08-17 (Wave 3 lane B, disclosure+authority gap repair).',
  },
]

export class StudioMcpBus {
  private registry = new CogoToolRegistry()
  private middlewares: Middleware[] = []
  private capabilityApprovalRequiredTools = new Set<string>()
  private capabilityApprovalVerifier: StudioCapabilityApprovalVerifier | null = null

  /**
   * Register a tool under the cogo.studio.* namespace.
   * @param shortName - Tool name without namespace prefix (e.g. 'addElement')
   * @param tool - Tool definition (handler, schema, description) without name
   */
  registerTool(shortName: string, tool: Omit<WebMCPTool, 'name'>): void {
    this.registry.register({
      ...tool,
      name: `${STUDIO_NS}${shortName}`,
    })
  }

  /** Add a middleware to the chain. Last added = outermost wrapper. */
  use(mw: Middleware): void {
    this.middlewares.push(mw)
  }

  /**
   * Install the fail-closed approval policy for creator-facing WebMCP writes.
   * Tool names are additive so separately registered Studio feature groups can
   * share one enforcement boundary.
   */
  requireCapabilityApproval(
    toolNames: Iterable<string>,
    verifier: StudioCapabilityApprovalVerifier,
  ): void {
    for (const toolName of toolNames) {
      this.capabilityApprovalRequiredTools.add(
        toolName.startsWith(STUDIO_NS) ? toolName : `${STUDIO_NS}${toolName}`,
      )
    }
    this.capabilityApprovalVerifier = verifier
  }

  /**
   * True when a `webmcp`-authority call to `toolName` must present a
   * verified, scoped approval grant before executing. Two layers, in order:
   *
   *  1. REFINEMENT (additive) — tools explicitly registered via
   *     `requireCapabilityApproval` (today: contract-derived, via
   *     `studio-capability-gateway.ts`'s `protectedCreatorMutationTools`).
   *     Can only ADD tools beyond the floor below; never narrows it.
   *  2. FLOOR (default) — once this bus has installed an approval verifier
   *     at all (i.e. `requireCapabilityApproval` has been called at least
   *     once, meaning this bus opts into the boundary), ANY tool registered
   *     on this bus's OWN registry that is not internal-tagged and not
   *     `readOnly: true` requires approval BY DEFAULT — regardless of
   *     whether a chat-operation contract exists for it. A contract's
   *     ABSENCE must never silently mean "not gated"; `WEBMCP_APPROVAL_OPT_OUT_TOOLS`
   *     above is the only sanctioned way to narrow this floor for a
   *     specific, reviewed tool.
   *
   * A bus that never calls `requireCapabilityApproval` (e.g. many unit-test
   * buses exercising unrelated mechanics) never installs this boundary and
   * is unaffected by layer 2 — it only activates once a verifier exists,
   * matching production wiring where `registerStudioCapabilityGatewayTools`
   * always installs one on the singleton `mcpBus`.
   */
  requiresCapabilityApproval(toolName: string): boolean {
    if (this.capabilityApprovalRequiredTools.has(toolName)) return true
    if (!this.capabilityApprovalVerifier) return false
    if (WEBMCP_APPROVAL_OPT_OUT_TOOLS.some((entry) => entry.tool === toolName)) return false
    const tool = this.registry.getTool(toolName)
    if (!tool) return false
    const tags = (tool.metadata?.tags as string[] | undefined) ?? []
    if (tags.includes('internal')) return false
    return tool.metadata?.readOnly !== true
  }

  /**
   * Execute a single MCP command through the middleware chain.
   * Validates namespace before execution.
   */
  async execute(
    cmd: McpCommand,
    options?: StudioMcpExecutionOptions,
  ): Promise<McpResult> {
    return this.executeWithAuthority(cmd, 'internal', options)
  }

  /** Execute a direct, user-initiated Studio UI command. */
  async executeTrustedUi(
    cmd: McpCommand,
    options?: StudioMcpExecutionOptions,
  ): Promise<McpResult> {
    return this.executeWithAuthority(cmd, 'studio-ui', options)
  }

  /**
   * Execute an externally supplied WebMCP command. Creator-facing writes are
   * rejected unless a scoped, payload-bound, one-time approval grant exists.
   */
  async executeWebMcp(
    cmd: McpCommand,
    options?: StudioMcpExecutionOptions,
  ): Promise<McpResult> {
    return this.executeWithAuthority(cmd, 'webmcp', options)
  }

  private async executeWithAuthority(
    cmd: McpCommand,
    authority: StudioMcpExecutionAuthority,
    options?: StudioMcpExecutionOptions,
  ): Promise<McpResult> {
    // Namespace validation
    if (!cmd.tool.startsWith(STUDIO_NS)) {
      return {
        success: false,
        error: `Invalid namespace: expected "${STUDIO_NS}*", got "${cmd.tool}"`,
      }
    }

    const requiresCapabilityApproval =
      authority === 'webmcp' &&
      this.requiresCapabilityApproval(cmd.tool)

    if (requiresCapabilityApproval && options?.signal?.aborted) {
      const reason = options.signal.reason ?? new DOMException('Aborted', 'AbortError')
      return {
        success: false,
        error: reason instanceof Error ? reason.message : String(reason),
      }
    }

    let executableCommand = cmd
    if (requiresCapabilityApproval) {
      if (!this.capabilityApprovalVerifier) {
        return {
          success: false,
          error: 'STUDIO_CAPABILITY_APPROVAL_BOUNDARY_UNAVAILABLE',
        }
      }
      try {
        this.capabilityApprovalVerifier(cmd)
      } catch (err: unknown) {
        return {
          success: false,
          error: err instanceof Error ? err.message : String(err),
        }
      }
      const params = { ...cmd.params }
      delete params[CAPABILITY_APPROVAL_TOKEN_KEY]
      executableCommand = { ...cmd, params }
    }

    // Build the innermost handler
    const innerHandler = async (): Promise<McpResult> => {
      try {
        const result = await this.registry.callTool(
          executableCommand.tool,
          executableCommand.params,
          options,
        )
        return { success: true, data: result }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err)
        return { success: false, error: message }
      }
    }

    // Chain middlewares: last-added middleware is outermost
    let chain = innerHandler
    for (const mw of this.middlewares) {
      const next = chain
      chain = () => mw(executableCommand, next)
    }

    const result = await chain()

    // Emit notification AFTER middleware chain (undo recording) completes
    if (result.success) {
      notificationBus.emit({
        type: 'mcp:notification',
        tool: executableCommand.tool,
        params: sanitizeNotificationParams(
          executableCommand.params,
        ) as Record<string, unknown>,
        result,
        timestamp: Date.now(),
      })
      // Awaited, not fire-and-forget: `emitCentralDisclosure` resolves the
      // store through a dynamic import (see `getStudioStore`), so awaiting is
      // what preserves the property the static import used to give for free —
      // the disclosure ChatMessage is appended BEFORE this execute resolves,
      // and an append that throws still propagates out of the execute. Both
      // are load-bearing: callers that push their own message after awaiting
      // an execute must still see the central card ahead of theirs.
      await this.emitCentralDisclosure(executableCommand, options)
    }

    return result
  }

  /**
   * Wave 4 lane 1 — the single seam that makes chat disclosure an
   * EXECUTION-MODEL property instead of a per-call-site courtesy (Slice 4's
   * "38 of 44 mutating bus call sites produce ZERO chat disclosure on
   * success" finding). Fires for every successful `execute`/`executeTrustedUi`/
   * `executeWebMcp` call, regardless of authority, unless:
   *
   *  - the tool is unknown to this bus's own registry (nothing to disclose),
   *  - the tool is tagged `internal` (undo/redo and other non-creator-facing
   *    plumbing — same tag `requiresCapabilityApproval` already excludes),
   *  - the tool declares `metadata.readOnly === true` (matches
   *    `isMutatingCommand`'s own fallback rule elsewhere in this codebase —
   *    reused here rather than reinvented, so a tool classified
   *    "not mutating" for approval purposes is the SAME tool classified
   *    "not mutating" for disclosure purposes), or
   *  - the caller set `options.siteHandlesDisclosure` (see that field's own
   *    doc comment for the structural, non-hand-listed suppression rule).
   */
  private async emitCentralDisclosure(
    cmd: McpCommand,
    options?: StudioMcpExecutionOptions,
  ): Promise<void> {
    if (options?.siteHandlesDisclosure) return

    const tool = this.registry.getTool(cmd.tool)
    if (!tool) return
    const tags = (tool.metadata?.tags as string[] | undefined) ?? []
    if (tags.includes('internal')) return
    if (tool.metadata?.readOnly === true) return

    const locale = getLocale()
    const copy = getChatMessagesCopy(locale).agentDisclosure
    const toolShortName = cmd.tool.startsWith(STUDIO_NS)
      ? cmd.tool.slice(STUDIO_NS.length)
      : cmd.tool
    const target = extractCheapTargetSummary(cmd.params)

    const msg: ChatMessage = {
      kind: 'agent-worklog',
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      // Baked verbatim in the current locale (see `StudioMcpExecutionOptions
      // .siteHandlesDisclosure`'s sibling doc comment on `agentDisclosure`
      // in chat-messages-copy.ts for why this happens HERE, at producer
      // time, rather than through the worklog copy dictionary).
      title: copy.title,
      card: 'agent-action',
      stage: 'dispatch',
      entries: [
        { label: copy.toolLabel, value: toolShortName, tone: 'success' },
        ...(target
          ? [{ label: copy.targetLabel, value: target, tone: 'neutral' as const }]
          : []),
      ],
    }

    // Via the dynamic getter, NOT a static `useStudioStore` import — see
    // `getStudioStore`'s doc comment above for why that static edge cannot
    // come back. The `await` at the single call site is what keeps this
    // observationally identical to the old synchronous call.
    const useStudioStore = await getStudioStore()
    useStudioStore.getState().appendChatMessage(msg)
  }

  /**
   * Execute multiple commands sequentially (order preserved).
   * Uses for...of loop, NOT Promise.all, to guarantee ordering.
   */
  async executeAll(
    cmds: McpCommand[],
    options?: StudioMcpExecutionOptions,
  ): Promise<McpResult[]> {
    const results: McpResult[] = []
    for (const cmd of cmds) {
      results.push(await this.execute(cmd, options))
    }
    return results
  }

  /** Get W3C-compatible manifest of all registered tools. */
  getManifest(): ToolManifestEntry[] {
    return this.registry.getManifest()
  }

  /** Cheap exact-name lookup without materializing the full manifest. */
  hasTool(name: string): boolean {
    return Boolean(this.registry.getTool(name))
  }

  /** Subscribe to tools-changed events. Returns unsubscribe function. */
  onToolsChanged(listener: ToolsChangedListener): () => void {
    return this.registry.onToolsChanged(listener)
  }
}

/** Singleton studio MCP bus instance. */
export const mcpBus = new StudioMcpBus()
